import {
  GaiaEventType,
  type ReviewProjectChatResumeRequest,
  type ReviewProjectChatStreamRequest,
  type GaiaEvent,
  type GeneralChatResumeRequest,
  type GeneralChatStreamRequest,
  type TemplatingChatResumeRequest,
  type TemplatingChatStreamRequest,
  type WebsocketChatCancelMessage,
  type WebsocketChatDoneMessage,
  type WebsocketChatErrorMessage,
  type WebsocketChatInterruptMessage,
  type WebsocketChatStatusMessage,
  type WebsocketChatThreadInfoMessage,
  type WebsocketChatTokenMessage,
  type WebsocketReviewProjectChatResumeMessage,
  type WebsocketReviewProjectChatStartMessage,
  type WebsocketEventMessage,
  type WebsocketGeneralChatResumeMessage,
  type WebsocketGeneralChatStartMessage,
  type WebsocketHeartbeatMessage,
  type WebsocketListSubscriptionsMessage,
  type WebsocketStatusMessage,
  type WebsocketSubscriptionMessage,
  WebsocketSubscriptionTarget,
  type WebsocketTemplatingChatResumeMessage,
  type WebsocketTemplatingChatStartMessage,
} from '@/api';
import { createWebsocketTicket } from '@/api/sdk.gen';
import { isAccessDenied } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';

export type WebSocketEventHandler = (event: GaiaEvent) => void;

/** Which chat backend a run targets — general, templating, or review-project. */
export type ChatKind = 'general' | 'templating' | 'review_project';

export type ChatStreamRequestBody =
  GeneralChatStreamRequest | TemplatingChatStreamRequest | ReviewProjectChatStreamRequest;
export type ChatResumeRequestBody =
  GeneralChatResumeRequest | TemplatingChatResumeRequest | ReviewProjectChatResumeRequest;

export type WebsocketChatIncomingMessage =
  | WebsocketChatTokenMessage
  | WebsocketChatThreadInfoMessage
  | WebsocketChatStatusMessage
  | WebsocketChatInterruptMessage
  | WebsocketChatDoneMessage
  | WebsocketChatErrorMessage;

export type WebsocketChatOutgoingMessage =
  | WebsocketGeneralChatStartMessage
  | WebsocketGeneralChatResumeMessage
  | WebsocketTemplatingChatStartMessage
  | WebsocketTemplatingChatResumeMessage
  | WebsocketReviewProjectChatStartMessage
  | WebsocketReviewProjectChatResumeMessage
  | WebsocketChatCancelMessage;

export type ChatRunHandler = (message: WebsocketChatIncomingMessage) => void;

/**
 * Why the server ended this socket for good. A socket is bound to the active
 * organisation at connect, so a switch (in any tab, or by an admin removing the
 * user or deleting the org) closes it; a deactivated or deleted user loses it
 * outright. `organisationUid` is null when the user is left without one.
 */
export type WebSocketControlEvent =
  | { kind: 'organisation_changed'; organisationUid: string | null }
  | { kind: 'access_revoked'; reason: string | null };

export type WebSocketControlListener = (event: WebSocketControlEvent) => void;

/** Close code for "your active organisation changed": reconnect with a fresh ticket. */
const ORGANISATION_CHANGED_CLOSE_CODE = 4001;
/** Close code for "your user was deactivated or deleted": never reconnect. */
const ACCESS_REVOKED_CLOSE_CODE = 4003;

export type WebsocketMessage =
  | WebsocketHeartbeatMessage
  | WebsocketSubscriptionMessage
  | WebsocketListSubscriptionsMessage
  | WebsocketEventMessage
  | WebsocketStatusMessage
  | WebsocketChatIncomingMessage
  | WebsocketChatOutgoingMessage;

const CHAT_INCOMING_TYPES = new Set([
  'chat_token',
  'chat_thread_info',
  'chat_status',
  'chat_interrupt',
  'chat_done',
  'chat_error',
]);

export interface WebSocketOptions {
  autoReconnect?: boolean; // default: true
  reconnectInterval?: number; // ms, default: 10_000
  onOpen?: () => void;
  onError?: (ev: Event) => void;
  onClose?: (ev: CloseEvent) => void;
}

const DEFAULT_WS_PATH = '/v1/app/technical/websocket/connect';

export class WebSocketService {
  private ws: WebSocket | null = null;
  private connectPromise: Promise<void> | null = null;
  private opts: Required<WebSocketOptions>;
  private handlers = new Map<GaiaEventType, Set<WebSocketEventHandler>>();
  private desiredSubscriptions = new Map<WebsocketSubscriptionTarget, Set<string>>();
  private subscriptionCounts = new Map<WebsocketSubscriptionTarget, Map<string, number>>();
  private chatRunHandlers = new Map<string, ChatRunHandler>();
  /** run_id → the last `stream_id` we handed to a consumer; the `chat_run` replay cursor. */
  private chatRunCursors = new Map<string, string>();
  private idleHeartbeatCount = 0;
  private closingForIdle = false;
  private suppressReconnectOnce = false;
  private debugListeners = new Set<(raw: string, parsed: WebsocketMessage | null) => void>();
  private outgoingDebugListeners = new Set<
    (raw: string, parsed: WebsocketMessage | null) => void
  >();
  private statusListeners = new Set<
    (status: 'open' | 'close' | 'error', detail?: Event | CloseEvent) => void
  >();
  private controlListeners = new Set<WebSocketControlListener>();
  /** The control event frame the server sends just before its closing 4001/4003. */
  private pendingControlEvent: WebSocketControlEvent | null = null;
  /** Set on 4003 (or a refused ticket): the user may no longer open a socket. */
  private revoked = false;
  /** Bumped on every org change, so a connect whose ticket predates it is dropped. */
  private orgGeneration = 0;
  private debugUrlOverride?: string;
  private debugTicketOverride?: string;

  constructor(opts: WebSocketOptions = {}) {
    this.opts = {
      autoReconnect: opts.autoReconnect ?? true,
      reconnectInterval: opts.reconnectInterval ?? 10_000,
      onOpen: opts.onOpen ?? ((): void => {}),
      onError: opts.onError ?? ((): void => {}),
      onClose: opts.onClose ?? ((): void => {}),
    };
  }

  async openConnection(): Promise<boolean> {
    await this.ensureConnected();
    if (this.isOpen()) {
      return true;
    }
    const ws = this.ws;
    if (!ws) {
      return false;
    }
    return this.waitForOpen(ws, 5_000);
  }

  closeConnection(): void {
    if (!this.ws) {
      return;
    }
    this.suppressReconnectOnce = true;
    this.ws.close(1000, 'Manual disconnect');
    this.ws = null;
  }

  /**
   * Close the socket before this tab changes the active organisation. The
   * socket's channels are bound to the old one, so its subscriptions go with
   * it; the event handlers stay, and the next subscribe (after the reload, or
   * an explicit {@link openConnection}) connects into the new organisation.
   *
   * @returns Whether a socket was open (or opening), i.e. worth reopening
   */
  disconnectForOrgChange(): boolean {
    const wasConnected = this.ws !== null || this.connectPromise !== null;
    this.orgGeneration += 1;
    // An in-flight connect now carries a stale ticket and drops itself; let
    // the next ensureConnected start over rather than wait on it.
    this.connectPromise = null;
    this.resetSubscriptions();
    this.closeConnection();
    // A ticket refused in the old context (e.g. no organisation yet) says
    // nothing about the new one; a revoked user is refused again at the ticket.
    this.revoked = false;
    return wasConnected;
  }

  /**
   * Listen for the server ending this socket because of an organisation or
   * access change. Those events only reach an open socket, so while anyone
   * listens the socket is kept open rather than closed as idle.
   */
  addControlListener(listener: WebSocketControlListener): () => void {
    this.controlListeners.add(listener);
    void this.ensureConnected();
    return () => this.controlListeners.delete(listener);
  }

  subscribe(
    target: WebsocketSubscriptionTarget,
    uid: string,
    handler: WebSocketEventHandler
  ): () => void {
    this.addHandler(target, handler);

    this.addSubscription(target, uid);

    void this.ensureConnected();
    return () => {
      this.unsubscribe(target, uid, handler);
    };
  }

  /**
   * Register an event handler for every event type a target emits, without
   * subscribing to any uid. Use together with {@link addSubscriptionUid} when a
   * single handler needs to track a dynamically changing set of uids (the vault
   * views watch the current directory + parent chain, or the files in a search
   * result). The returned disposer removes only this handler.
   */
  registerHandler(target: WebsocketSubscriptionTarget, handler: WebSocketEventHandler): () => void {
    const eventTypes = this.addHandler(target, handler);
    void this.ensureConnected();
    return () => {
      for (const eventType of eventTypes) {
        const handlers = this.handlers.get(eventType);
        if (!handlers) {
          continue;
        }
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.handlers.delete(eventType);
        }
      }
    };
  }

  /**
   * Subscribe to a single (target, uid) pair, refcounted across callers. Sends a
   * subscribe frame the first time a uid is requested and an unsubscribe frame
   * once the last caller disposes. Pair with {@link registerHandler} to receive
   * the events.
   */
  addSubscriptionUid(target: WebsocketSubscriptionTarget, uid: string): () => void {
    this.addSubscription(target, uid);
    void this.ensureConnected();
    return () => {
      this.removeSubscription(target, uid);
    };
  }

  private addHandler(
    target: WebsocketSubscriptionTarget,
    handler: WebSocketEventHandler
  ): GaiaEventType[] {
    const eventTypes = this.getEventTypesForTarget(target);
    for (const eventType of eventTypes) {
      const handlers = this.handlers.get(eventType) ?? new Set<WebSocketEventHandler>();
      handlers.add(handler);
      this.handlers.set(eventType, handlers);
    }
    return eventTypes;
  }

  registerChatRun(runId: string, handler: ChatRunHandler): () => void {
    if (!runId) {
      throw new Error('registerChatRun requires a non-empty runId');
    }
    this.chatRunHandlers.set(runId, handler);
    this.idleHeartbeatCount = 0;
    void this.ensureConnected();
    return () => {
      this.unregisterChatRun(runId);
    };
  }

  unregisterChatRun(runId: string): void {
    this.chatRunHandlers.delete(runId);
    // Nobody is consuming this run any more, so the cursor has nothing to protect:
    // a later re-attach opens a fresh bubble and wants the full replay again.
    this.chatRunCursors.delete(runId);
  }

  sendChatStart(runId: string, data: ChatStreamRequestBody, kind: ChatKind): boolean {
    let message: WebsocketChatOutgoingMessage;
    switch (kind) {
      case 'templating':
        message = {
          type: 'templating_chat_start',
          run_id: runId,
          data,
        };
        break;
      case 'review_project':
        message = {
          type: 'review_project_chat_start',
          run_id: runId,
          data,
        };
        break;
      default:
        message = {
          type: 'general_chat_start',
          run_id: runId,
          data,
        };
    }
    return this.sendMessage(message);
  }

  sendChatResume(runId: string, data: ChatResumeRequestBody, kind: ChatKind): boolean {
    let message: WebsocketChatOutgoingMessage;
    switch (kind) {
      case 'templating':
        message = {
          type: 'templating_chat_resume',
          run_id: runId,
          data,
        };
        break;
      case 'review_project':
        message = {
          type: 'review_project_chat_resume',
          run_id: runId,
          data,
        };
        break;
      default:
        message = {
          type: 'general_chat_resume',
          run_id: runId,
          data,
        };
    }
    return this.sendMessage(message);
  }

  sendChatCancel(runId: string): boolean {
    return this.sendMessage({ type: 'chat_cancel', run_id: runId });
  }

  unsubscribe(
    target: WebsocketSubscriptionTarget,
    uid: string,
    handler?: WebSocketEventHandler
  ): void {
    for (const eventType of this.getEventTypesForTarget(target)) {
      const handlers = this.handlers.get(eventType);
      if (!handlers) {
        continue;
      }

      if (handler) {
        handlers.delete(handler);
      } else {
        handlers.clear();
      }

      if (handlers.size === 0) {
        this.handlers.delete(eventType);
      }
    }
    this.removeSubscription(target, uid);
  }

  private isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private waitForOpen(ws: WebSocket, timeoutMs: number): Promise<boolean> {
    if (ws.readyState === WebSocket.OPEN) {
      return Promise.resolve(true);
    }
    if (ws.readyState === WebSocket.CLOSED) {
      return Promise.resolve(false);
    }
    return new Promise(resolve => {
      let settled = false;
      const onOpen = (): void => {
        finish(true);
      };
      const onError = (): void => {
        finish(false);
      };
      const onClose = (): void => {
        finish(false);
      };
      const finish = (result: boolean): void => {
        if (settled) {
          return;
        }
        settled = true;
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        ws.removeEventListener('close', onClose);
        clearTimeout(timer);
        resolve(result);
      };
      const timer = setTimeout(() => {
        finish(ws.readyState === WebSocket.OPEN);
      }, timeoutMs);
      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onError);
      ws.addEventListener('close', onClose);
    });
  }

  private async ensureConnected(): Promise<void> {
    if (this.revoked) {
      return;
    }
    const readyState = this.ws?.readyState;
    if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) {
      return;
    }
    // Serialize connection attempts. `getTicket()` awaits, so several callers
    // (e.g. registerHandler + addSubscriptionUid firing together) could all pass
    // the guard above and each create a socket, leaving orphaned connections the
    // client never heartbeats on. Share one in-flight connect instead.
    if (this.connectPromise) {
      return this.connectPromise;
    }
    const connecting = this.connect().finally(() => {
      // An org change may have replaced this attempt; don't clear its successor.
      if (this.connectPromise === connecting) {
        this.connectPromise = null;
      }
    });
    this.connectPromise = connecting;
    return connecting;
  }

  private async connect(): Promise<void> {
    const generation = this.orgGeneration;
    let ticket: string;
    try {
      ticket = await this.getTicket();
    } catch (error) {
      if (isAccessDenied(error)) {
        // Signed out, deactivated or without an organisation: retrying every
        // few seconds can't fix that, the auth flow has to.
        logger.warn('[WebSocketService] Websocket ticket refused', error);
        this.revoked = true;
        return;
      }
      logger.error('[WebSocketService] Failed to fetch a websocket ticket', error);
      this.scheduleReconnect();
      return;
    }

    // The organisation changed while we awaited the ticket: it is bound to the
    // old one. Whoever changed it reconnects (or reloads) into the new one.
    if (generation !== this.orgGeneration) {
      return;
    }

    // A socket may have opened while we awaited the ticket.
    const readyState = this.ws?.readyState;
    if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) {
      return;
    }

    const wsUrl = this.buildWsUrl(ticket);
    this.ws = new WebSocket(wsUrl);

    this.ws.addEventListener('open', () => {
      this.idleHeartbeatCount = 0;
      this.closingForIdle = false;
      this.opts.onOpen();
      for (const listener of this.statusListeners) listener('open');
      this.flushSubscriptions();
    });

    this.ws.addEventListener('message', event => {
      void this.handleMessage(event);
    });

    this.ws.addEventListener('error', event => {
      this.opts.onError(event);
      for (const listener of this.statusListeners) listener('error', event);
    });

    this.ws.addEventListener('close', event => {
      this.ws = null;
      this.opts.onClose(event);
      for (const listener of this.statusListeners) listener('close', event);
      const controlEvent = this.takeControlEvent(event.code);
      if (controlEvent) {
        this.handleControlClose(controlEvent);
        return;
      }
      if (this.suppressReconnectOnce) {
        this.suppressReconnectOnce = false;
        this.closingForIdle = false;
        return;
      }
      if (!this.closingForIdle) {
        this.scheduleReconnect();
      }
      this.closingForIdle = false;
    });
  }

  private scheduleReconnect(): void {
    if (this.opts.autoReconnect) {
      setTimeout(() => void this.ensureConnected(), this.opts.reconnectInterval);
    }
  }

  /**
   * The control event behind a 4001/4003 close. The server forwards the event
   * frame first so we learn the target organisation or the reason; if that
   * frame didn't make it, the close code alone still says what happened.
   */
  private takeControlEvent(code: number): WebSocketControlEvent | null {
    const pending = this.pendingControlEvent;
    this.pendingControlEvent = null;
    if (code === ORGANISATION_CHANGED_CLOSE_CODE) {
      return pending?.kind === 'organisation_changed'
        ? pending
        : { kind: 'organisation_changed', organisationUid: null };
    }
    if (code === ACCESS_REVOKED_CLOSE_CODE) {
      return pending?.kind === 'access_revoked'
        ? pending
        : { kind: 'access_revoked', reason: null };
    }
    return null;
  }

  private handleControlClose(controlEvent: WebSocketControlEvent): void {
    this.suppressReconnectOnce = false;
    this.closingForIdle = false;
    // Every subscription belonged to the old organisation (or a user who no
    // longer has one); the server keeps none of it, so neither do we.
    this.resetSubscriptions();
    if (controlEvent.kind === 'access_revoked') {
      this.revoked = true;
    }
    for (const listener of this.controlListeners) listener(controlEvent);
    if (controlEvent.kind === 'organisation_changed') {
      // Straight back in, with a fresh ticket bound to the new organisation.
      void this.ensureConnected();
    }
  }

  /** Drop every org-scoped subscription. Event handlers are kept. */
  private resetSubscriptions(): void {
    this.desiredSubscriptions.clear();
    this.subscriptionCounts.clear();
    this.chatRunHandlers.clear();
    this.chatRunCursors.clear();
  }

  /** A single-use ticket for one connect, bound to the active organisation. */
  private async getTicket(): Promise<string> {
    if (this.debugTicketOverride !== undefined) {
      return this.debugTicketOverride;
    }
    const { data } = await createWebsocketTicket({ throwOnError: true });
    return data.ticket;
  }

  private buildWsUrl(ticket: string): string {
    const baseUrl = this.debugUrlOverride ?? this.buildDefaultWsUrl();
    if (!ticket) {
      return baseUrl;
    }
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}ticket=${encodeURIComponent(ticket)}`;
  }

  private sendMessage(message: WebsocketMessage): boolean {
    if (!this.isOpen()) {
      return false;
    }
    const raw = JSON.stringify(message);
    this.notifyOutgoingDebugListeners(raw, message);
    this.ws?.send(raw);
    return true;
  }

  private sendSubscribe(target: WebsocketSubscriptionTarget, uid: string): void {
    if (!this.isOpen()) {
      return;
    }
    // A `chat_run` subscribe replays the run's frames, and `flushSubscriptions`
    // re-sends every subscription on each socket open. Without a cursor a
    // mid-run reconnect would replay from the first entry into a bubble that
    // already shows it — so resume just past the last frame we processed. A
    // fresh attach has no cursor and gets the full replay, which is what it wants.
    const resumeFrom =
      target === WebsocketSubscriptionTarget.CHAT_RUN ? this.chatRunCursors.get(uid) : undefined;
    this.sendMessage({
      type: 'subscribe',
      target,
      uid,
      ...(resumeFrom ? { resume_from: resumeFrom } : {}),
    });
  }

  private sendUnsubscribe(target: WebsocketSubscriptionTarget, uid: string): void {
    if (!this.isOpen()) {
      return;
    }
    this.sendMessage({ type: 'unsubscribe', target, uid });
  }

  private flushSubscriptions(): void {
    if (!this.isOpen()) {
      return;
    }
    for (const [target, uids] of this.desiredSubscriptions.entries()) {
      for (const uid of uids) this.sendSubscribe(target, uid);
    }
    this.resubscribeChatRuns();
  }

  /**
   * A reconnect hands us a server with empty per-connection state: every chat_run
   * pump the dropped socket owned was cancelled with it, including the implicit
   * one a self-started run gets from its `*_chat_start` frame. Those runs never
   * entered `desiredSubscriptions` — they stream without the client ever
   * subscribing — so the loop above can't re-arm them. Do it here from their
   * cursors, which delivers exactly the gap: readers carry their own cursor and
   * reading doesn't consume, so even a terminal `chat_done` the dead pump
   * already read is still there to be re-delivered.
   */
  private resubscribeChatRuns(): void {
    const alreadySubscribed = this.desiredSubscriptions.get(WebsocketSubscriptionTarget.CHAT_RUN);
    for (const runId of this.chatRunHandlers.keys()) {
      if (alreadySubscribed?.has(runId)) continue;
      // No cursor means no frame has arrived for this run yet, so we can't tell a
      // live run from one whose start frame is still in flight — and subscribing
      // to a run the server hasn't spawned would be pointless. Leave it; the
      // post-run thread GET is the recovery for that window.
      if (!this.chatRunCursors.has(runId)) continue;
      this.sendSubscribe(WebsocketSubscriptionTarget.CHAT_RUN, runId);
    }
  }

  private async handleMessage(event: MessageEvent): Promise<void> {
    const rawText = await this.readEventData(event.data);
    if (!rawText) {
      return;
    }
    this.handleRawMessage(rawText);
  }

  private handleRawMessage(rawText: string): void {
    let message: WebsocketMessage | null = null;
    try {
      message = JSON.parse(rawText) as WebsocketMessage;
    } catch (error) {
      logger.warn('[WebSocketService] parse error', error);
      this.notifyDebugListeners(rawText, null);
      return;
    }

    this.notifyDebugListeners(rawText, message);

    if (message.type && CHAT_INCOMING_TYPES.has(message.type)) {
      const chatMessage = message as WebsocketChatIncomingMessage;
      const handler = this.chatRunHandlers.get(chatMessage.run_id);
      if (handler) {
        this.idleHeartbeatCount = 0;
        // Advance the replay cursor only for frames that reach a consumer, and
        // before dispatching: a `chat_done` handler tears the run down, and
        // recording afterwards would resurrect the entry it just deleted.
        if (chatMessage.stream_id) {
          this.chatRunCursors.set(chatMessage.run_id, chatMessage.stream_id);
        }
        handler(chatMessage);
      }
      return;
    }

    if (message.type === 'heartbeat') {
      if (message.data === 'ping') {
        this.sendMessage({ type: 'heartbeat', data: 'pong' });
      }
      if (
        this.getActiveSubscriptionCount() === 0 &&
        this.chatRunHandlers.size === 0 &&
        this.controlListeners.size === 0
      ) {
        this.idleHeartbeatCount += 1;
        if (this.idleHeartbeatCount >= 5) {
          this.closeForIdle();
        }
      } else {
        this.idleHeartbeatCount = 0;
      }
      return;
    }

    if (message.type === 'list_subscriptions' && typeof message.data === 'object') {
      return;
    }

    if (message.type === 'event') {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- `message` is JSON.parse output cast to WebsocketMessage; a malformed frame can carry `data: null` at runtime
      if (typeof message.data !== 'object' || message.data === null) {
        logger.warn('[WebSocketService] Event message missing payload');
        return;
      }
      const gaiaEvent = message.data;
      const controlEvent = this.parseControlEvent(gaiaEvent);
      if (controlEvent) {
        this.pendingControlEvent = controlEvent;
        return;
      }
      const handlers = this.handlers.get(gaiaEvent.gaia_event_type);
      if (!handlers || handlers.size === 0) {
        return;
      }
      for (const handler of handlers) handler(gaiaEvent);
      return;
    }

    if (message.type === 'status') {
      logger.info('[WebSocketService] Status from server', message.message, message.level);
    }
  }

  private parseControlEvent(gaiaEvent: GaiaEvent): WebSocketControlEvent | null {
    // Both control events carry a GaiaEventObjectPayload; read its fields loosely.
    const payload = 'data' in gaiaEvent.data ? gaiaEvent.data.data : {};
    const field = (key: string): string | null => {
      const value = payload[key];
      return typeof value === 'string' ? value : null;
    };
    switch (gaiaEvent.gaia_event_type) {
      case GaiaEventType.ACTIVE_ORGANISATION_CHANGED:
        return { kind: 'organisation_changed', organisationUid: field('organisation_uid') };
      case GaiaEventType.USER_ACCESS_REVOKED:
        return { kind: 'access_revoked', reason: field('reason') };
      default:
        return null;
    }
  }

  private async readEventData(data: MessageEvent['data']): Promise<string | null> {
    if (typeof data === 'string') {
      return data;
    }
    if (data instanceof Blob) {
      return data.text();
    }
    if (data instanceof ArrayBuffer) {
      return new TextDecoder().decode(data);
    }
    return data ? String(data) : null;
  }

  private closeForIdle(): void {
    logger.info('[WebSocketService] Close idle');
    if (!this.ws || this.closingForIdle) {
      return;
    }
    this.closingForIdle = true;
    this.ws.close(1000, 'Idle with no subscriptions');
  }

  private buildDefaultWsUrl(): string {
    const apiUrlObj = new URL(import.meta.env.VITE_API_URL);
    const wsProtocol = apiUrlObj.protocol === 'https:' ? 'wss' : 'ws';
    return `${wsProtocol}://${apiUrlObj.host}${DEFAULT_WS_PATH}`;
  }

  private getEventTypesForTarget(target: WebsocketSubscriptionTarget): GaiaEventType[] {
    switch (target) {
      case WebsocketSubscriptionTarget.REVIEW:
        return [GaiaEventType.REVIEW_UPDATE];
      case WebsocketSubscriptionTarget.VAULT_FILE:
        return [
          GaiaEventType.VAULT_FILE_UPDATED,
          GaiaEventType.VAULT_FILE_DELETED,
          GaiaEventType.CONTRACT_MANAGEMENT_MODEL_EXTRACTION_UPDATED,
        ];
      case WebsocketSubscriptionTarget.VAULT_DIRECTORY:
        return [GaiaEventType.VAULT_DIRECTORY_UPDATED, GaiaEventType.VAULT_DIRECTORY_DELETED];
      case WebsocketSubscriptionTarget.PLAYBOOK:
        return [GaiaEventType.PLAYBOOK_UPDATED, GaiaEventType.PLAYBOOK_RUN_CREATED];
      case WebsocketSubscriptionTarget.PLAYBOOK_RUN:
        return [GaiaEventType.PLAYBOOK_RUN_UPDATED];
      case WebsocketSubscriptionTarget.SIGNING_REQUEST:
        return [GaiaEventType.SIGNING_REQUEST_UPDATED];
      case WebsocketSubscriptionTarget.SIGNING_REQUESTS:
        return [GaiaEventType.SIGNING_REQUESTS_UPDATED];
      case WebsocketSubscriptionTarget.REVIEW_PROJECT:
        return [
          GaiaEventType.REVIEW_PROJECT_ANALYSIS_CREATED,
          GaiaEventType.REVIEW_PROJECT_ANALYSIS_UPDATED,
          GaiaEventType.REVIEW_PROJECT_ANALYSIS_DELETED,
          GaiaEventType.REVIEW_PROJECT_REPORT_UPDATED,
          GaiaEventType.REVIEW_PROJECT_QUESTION_CREATED,
          GaiaEventType.REVIEW_PROJECT_QUESTION_UPDATED,
          GaiaEventType.REVIEW_PROJECT_QUESTION_DELETED,
        ];
      case WebsocketSubscriptionTarget.REVIEW_PROJECT_FILE:
        return [
          GaiaEventType.REVIEW_PROJECT_FILE_UPDATED,
          GaiaEventType.REVIEW_PROJECT_FILE_SEEN_UPDATED,
        ];
      case WebsocketSubscriptionTarget.VAULT_ZIP_DOWNLOAD:
        return [GaiaEventType.VAULT_ZIP_DOWNLOAD_UPDATED];
      case WebsocketSubscriptionTarget.DEMO_DATA_SEED:
        return [GaiaEventType.DEMO_DATA_SEED_UPDATED];
      case WebsocketSubscriptionTarget.USER_ATTENTION:
        return [GaiaEventType.USER_ATTENTION];
      case WebsocketSubscriptionTarget.CHAT_RUN:
        // A `chat_run` subscription only reopens the stream of a run started
        // elsewhere; its frames are the `chat_*` types routed by `run_id`
        // through `chatRunHandlers`, never the generic `event` fan-out.
        return [];
      default:
        return [GaiaEventType.CONTRACT_MANAGEMENT_EXTRACTION_UPDATE];
    }
  }

  private getSubscriptionBucket(target: WebsocketSubscriptionTarget): Set<string> {
    const existing = this.desiredSubscriptions.get(target);
    if (existing) {
      return existing;
    }
    const next = new Set<string>();
    this.desiredSubscriptions.set(target, next);
    return next;
  }

  private getSubscriptionCountBucket(target: WebsocketSubscriptionTarget): Map<string, number> {
    const existing = this.subscriptionCounts.get(target);
    if (existing) {
      return existing;
    }
    const next = new Map<string, number>();
    this.subscriptionCounts.set(target, next);
    return next;
  }

  private addSubscription(target: WebsocketSubscriptionTarget, uid: string): void {
    if (!uid) {
      return;
    }
    const bucket = this.getSubscriptionBucket(target);
    const counts = this.getSubscriptionCountBucket(target);
    const currentCount = counts.get(uid) ?? 0;
    counts.set(uid, currentCount + 1);
    if (!bucket.has(uid)) {
      bucket.add(uid);
      this.idleHeartbeatCount = 0;
      this.sendSubscribe(target, uid);
    }
  }

  private removeSubscription(target: WebsocketSubscriptionTarget, uid: string): void {
    if (!uid) {
      return;
    }
    const bucket = this.desiredSubscriptions.get(target);
    const counts = this.subscriptionCounts.get(target);
    if (!bucket || !counts) {
      return;
    }
    const currentCount = counts.get(uid) ?? 0;
    if (currentCount <= 1) {
      counts.delete(uid);
      bucket.delete(uid);
      this.idleHeartbeatCount = 0;
      this.sendUnsubscribe(target, uid);
    } else {
      counts.set(uid, currentCount - 1);
    }
    if (bucket.size === 0) {
      this.desiredSubscriptions.delete(target);
    }
    if (counts.size === 0) {
      this.subscriptionCounts.delete(target);
    }
  }

  private getActiveSubscriptionCount(): number {
    let count = 0;
    for (const uids of this.desiredSubscriptions.values()) {
      count += uids.size;
    }
    return count;
  }

  private notifyDebugListeners(rawText: string, message: WebsocketMessage | null): void {
    for (const listener of this.debugListeners) listener(rawText, message);
  }

  private notifyOutgoingDebugListeners(rawText: string, message: WebsocketMessage | null): void {
    for (const listener of this.outgoingDebugListeners) listener(rawText, message);
  }

  addDebugListener(listener: (raw: string, parsed: WebsocketMessage | null) => void): () => void {
    this.debugListeners.add(listener);
    return () => this.debugListeners.delete(listener);
  }

  addOutgoingDebugListener(
    listener: (raw: string, parsed: WebsocketMessage | null) => void
  ): () => void {
    this.outgoingDebugListeners.add(listener);
    return () => this.outgoingDebugListeners.delete(listener);
  }

  addStatusListener(
    listener: (status: 'open' | 'close' | 'error', detail?: Event | CloseEvent) => void
  ): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  sendRaw(raw: string): boolean {
    if (!this.isOpen()) {
      return false;
    }
    let parsed: WebsocketMessage | null = null;
    try {
      parsed = JSON.parse(raw) as WebsocketMessage;
    } catch {
      parsed = null;
    }
    this.notifyOutgoingDebugListeners(raw, parsed);
    this.ws?.send(raw);
    return true;
  }

  sendStructured(message: WebsocketMessage): boolean {
    this.sendMessage(message);
    return this.isOpen();
  }

  setDebugConfig(config: { url?: string; ticket?: string | null }): void {
    if (config.url === undefined) {
      this.debugUrlOverride = undefined;
    } else {
      this.debugUrlOverride = config.url;
    }

    if (config.ticket === undefined) {
      this.debugTicketOverride = undefined;
    } else {
      this.debugTicketOverride = config.ticket ?? '';
    }
  }
}

let wsClientSingleton: WebSocketService | null = null;

export const getWsClient = (): WebSocketService => {
  wsClientSingleton ??= new WebSocketService();
  return wsClientSingleton;
};
